import { useEffect, useState } from "react";
import { IdCard, Landmark, MapPin, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DetailField, DetailGrid, DetailSection } from "@/components/ui/detail-field";
import { FilaCampo } from "@/components/ui/modal-secciones";
import { Req } from "@/components/ui/modal-form";
import { EmptyState, InlineLoading } from "@/components/ui/data-states";

/** Lo que se edita desde "Mis datos" (mismo alcance que tenía el formulario de usuario). */
export interface DatosEditables {
  nombre_legal: string;
  telefono: string | null;
  clave_pais_telefono: string;
  curp: string | null;
}

/** id del formulario: el botón Guardar vive en el encabezado de la sección (`form=`). */
export const FORM_MIS_DATOS = "mc-form-datos";

const CURP_RE = /^[A-Z][AEIOUX][A-Z]{2}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;

const fecha = (v?: string | null) =>
  v ? new Date(`${v.slice(0, 10)}T12:00:00`).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }) : null;

const unir = (partes: (string | null | undefined)[]) =>
  partes.map((p) => (p ?? "").toString().trim()).filter(Boolean).join(", ") || null;

/**
 * Mis datos: se leen en secciones (personales, domicilio, fiscales) y lo personal se edita aquí
 * mismo, sin abrir otro modal. Domicilio y datos fiscales son de consulta: los captura quien lleva
 * tu expediente.
 */
export function SeccionDatos({
  persona,
  cargando,
  correo,
  guardando,
  creando,
  onCrear,
  onGuardar,
  editando,
  onEditando,
  soloLectura = false,
}: {
  // Fila de `personas` (select *); el tipo generado no cubre todas las columnas.
  persona: any | null;
  cargando: boolean;
  correo?: string | null;
  guardando: boolean;
  creando: boolean;
  onCrear: () => void;
  /** Resuelve true si se guardó (cierra la edición). */
  onGuardar: (d: DatosEditables) => Promise<boolean>;
  /** La edición la controla el modal: Editar / Cancelar / Guardar van en el encabezado. */
  editando: boolean;
  onEditando: (v: boolean) => void;
  /** "Ver como": se consulta la ficha de otra persona; sin crear ni editar. */
  soloLectura?: boolean;
}) {
  const [nombre, setNombre] = useState("");
  const [lada, setLada] = useState("MX");
  const [telefono, setTelefono] = useState("");
  const [curp, setCurp] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editando || !persona) return;
    setNombre(persona.nombre_legal ?? "");
    setLada(persona.clave_pais_telefono || "MX");
    setTelefono(persona.telefono ?? "");
    setCurp(persona.curp ?? "");
    setError(null);
  }, [editando, persona]);

  if (cargando) return <InlineLoading label={soloLectura ? "Cargando datos..." : "Cargando tus datos..."} />;

  if (!persona) {
    return (
      <div>
        <EmptyState
          icono={IdCard}
          titulo={soloLectura ? "Sin datos personales" : "Aún no tienes datos personales"}
          texto={soloLectura ? "Esta persona no tiene ficha de datos personales." : "Crea tu ficha para registrar tu nombre, teléfono y CURP."}
        />
        {!soloLectura && (
          <div className="flex justify-center">
            <Button variant="primary-outline" size="compact" onClick={onCrear} disabled={creando}>
              <UserPlus /> {creando ? "Creando..." : "Completar mis datos"}
            </Button>
          </div>
        )}
      </div>
    );
  }

  const guardar = async () => {
    const curpLimpia = curp.trim().toUpperCase();
    if (!nombre.trim()) return setError("Escribe tu nombre completo.");
    if (telefono && telefono.length !== 10) return setError("El teléfono lleva 10 dígitos.");
    if (curpLimpia && !CURP_RE.test(curpLimpia)) return setError("La CURP no tiene el formato correcto (18 caracteres).");
    setError(null);
    const ok = await onGuardar({
      nombre_legal: nombre.trim(),
      telefono: telefono || null,
      clave_pais_telefono: lada || "MX",
      curp: curpLimpia || null,
    });
    if (ok) onEditando(false);
  };

  const telefonoTxt = persona.telefono ? `+${persona.clave_pais_telefono === "MX" || !persona.clave_pais_telefono ? "52" : "1"} ${persona.telefono}` : null;

  if (editando) {
    return (
      <form
        id={FORM_MIS_DATOS}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void guardar();
        }}
      >
        <DetailSection icon={IdCard} title="Datos personales" description="Nombre, teléfono y CURP. El correo es tu usuario y no se cambia aquí.">
          <FilaCampo id="mc-nombre" label={<>Nombre completo <Req /></>}>
            <Input id="mc-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="name" maxLength={200} autoFocus />
          </FilaCampo>
          <FilaCampo id="mc-correo" label="Correo">
            <Input id="mc-correo" value={persona.email || correo || ""} readOnly disabled />
          </FilaCampo>
          <FilaCampo id="mc-telefono" label="Teléfono">
            <div className="flex gap-2">
              <Select value={lada} onValueChange={setLada}>
                <SelectTrigger className="w-24 shrink-0" aria-label="Lada del país">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MX">+52 MX</SelectItem>
                  <SelectItem value="US">+1 US</SelectItem>
                  <SelectItem value="CA">+1 CA</SelectItem>
                </SelectContent>
              </Select>
              <Input
                id="mc-telefono"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10 dígitos"
              />
            </div>
          </FilaCampo>
          <FilaCampo id="mc-curp" label="CURP" ayuda="18 caracteres, como en tu identificación.">
            <Input
              id="mc-curp"
              value={curp}
              onChange={(e) => setCurp(e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 18))}
              className="font-mono uppercase"
              placeholder="18 caracteres"
            />
          </FilaCampo>
          {error && (
            <p role="alert" className="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive">{error}</p>
          )}
        </DetailSection>
      </form>
    );
  }

  return (
    <>
      <DetailSection icon={IdCard} title="Datos personales">
        <DetailGrid>
          <DetailField label="Nombre completo" value={persona.nombre_legal} vacio="Sin nombre" />
          <DetailField label="Correo" value={persona.email || correo} vacio="Sin correo" />
          <DetailField label="Teléfono" value={telefonoTxt} tabular vacio="Sin teléfono" />
          <DetailField label="CURP" value={persona.curp} mono vacio="Sin CURP" />
          <DetailField label="Fecha de nacimiento" value={fecha(persona.fecha_nacimiento)} vacio="Sin fecha" />
          <DetailField label="Ocupación" value={persona.ocupacion} vacio="Sin ocupación" />
        </DetailGrid>
      </DetailSection>

      <DetailSection icon={MapPin} title="Domicilio">
        <DetailGrid>
          <DetailField
            ancho
            label="Calle y número"
            value={unir([persona.direccion_calle, persona.direccion_num_ext && `#${persona.direccion_num_ext}`, persona.direccion_num_int && `int. ${persona.direccion_num_int}`])?.replace(/, #/g, " #").replace(/, int\./g, " int.")}
            vacio="Sin calle capturada"
          />
          <DetailField label="Colonia" value={persona.direccion_colonia} vacio="Sin colonia" />
          <DetailField
            label="Código postal"
            value={persona.direccion_codigo_postal ? String(persona.direccion_codigo_postal).trim() : null}
            tabular
            vacio="Sin código postal"
          />
        </DetailGrid>
      </DetailSection>

      <DetailSection icon={Landmark} title="Datos fiscales" description="Domicilio y datos fiscales los actualiza quien lleva tu expediente.">
        <DetailGrid>
          <DetailField label="RFC" value={persona.rfc} mono vacio="Sin RFC" />
          <DetailField label="Régimen fiscal" value={persona.regimen} vacio="Sin régimen" />
          <DetailField label="Uso de CFDI" value={persona.uso_cfdi} vacio="Sin uso de CFDI" />
          <DetailField
            label="C.P. fiscal"
            value={persona.direccion_fiscal_codigo_postal ? String(persona.direccion_fiscal_codigo_postal).trim() : null}
            tabular
            vacio="Sin C.P. fiscal"
          />
        </DetailGrid>
      </DetailSection>
    </>
  );
}
